/**
 * @jsxRuntime classic
 * @jsx jsx
 */

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { fg } from '@atlaskit/platform-feature-flags';
var heights = {
  default: "".concat(32 / 14, "em"),
  compact: "".concat(24 / 14, "em")
};
import { SplitButtonContext } from './split-button-context';
import { getActions } from './utils';
var baseDividerStyles = css({
  width: "var(--ds-border-width, 1px)",
  position: 'relative',
  // This is 1 so it appears above buttons by default.
  // When buttons are selected they have a zIndex of 2 applied.
  // See use-button-base.tsx.
  zIndex: 1
});
var defaultDividerStyles = css({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
  height: heights.default
});
var compactDividerStyles = css({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
  height: heights.compact
});
var dividerDisabledStyles = css({
  backgroundColor: "var(--ds-text-disabled, #091E4224)",
  cursor: 'not-allowed',
  opacity: 0.51
});
var dividerDisabledRefreshedStyles = css({
  backgroundColor: "var(--ds-border-disabled, #091E4224)",
  cursor: 'not-allowed'
});
var dividerAppearance = {
  default: css({
    backgroundColor: "var(--ds-text, #172B4D)",
    opacity: 0.51
  }),
  primary: css({
    backgroundColor: "var(--ds-text-inverse, #FFF)",
    opacity: 0.64
  }),
  navigation: css({
    height: '16px',
    margin: "var(--ds-space-100, 8px)".concat(" -0.5px"),
    backgroundColor: "var(--ds-text-subtle, #0052cc)",
    opacity: 0.62
  })
};
var dividerRefreshedOffsetStyles = css({
  marginInline: "calc(0px - ".concat("var(--ds-border-width, 1px)", ")")
});
var dividerHeight = {
  default: defaultDividerStyles,
  compact: compactDividerStyles
};
/**
 * TODO: Add JSDoc
 */
export var Divider = function Divider(_ref) {
  var appearance = _ref.appearance,
    spacing = _ref.spacing,
    _ref$isDisabled = _ref.isDisabled,
    isDisabled = _ref$isDisabled === void 0 ? false : _ref$isDisabled;
  return (
    // I find it funny to provide a div for Divider
    jsx("div", {
      css: [baseDividerStyles, dividerHeight[spacing], isDisabled && (fg('platform-component-visual-refresh') ? dividerDisabledRefreshedStyles : dividerDisabledStyles), !isDisabled && dividerAppearance[appearance], fg('platform-component-visual-refresh') && dividerRefreshedOffsetStyles]
    })
  );
};
var splitButtonStyles = css({
  display: 'inline-flex',
  position: 'relative',
  alignItems: 'center',
  whiteSpace: 'nowrap'
});
var primaryButtonStyles = css({
  // eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  'button,a': {
    borderEndEndRadius: 0,
    borderStartEndRadius: 0
  }
});
var secondaryButtonStyles = css({
  // eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  'button,a': {
    borderEndStartRadius: 0,
    borderStartStartRadius: 0
  }
});
var defaultAppearanceContainerStyles = css({
  borderRadius: "var(--ds-border-radius, 3px)",
  outlineColor: "var(--ds-border, #091E4224)",
  outlineOffset: "calc(0px - ".concat("var(--ds-border-width, 1px)", ")"),
  outlineStyle: 'solid',
  outlineWidth: "var(--ds-border-width, 1px)"
});

/**
 * TODO: Add JSdoc
 */
export var SplitButtonContainer = function SplitButtonContainer(_ref2) {
  var appearance = _ref2.appearance,
    children = _ref2.children,
    _ref2$isDisabled = _ref2.isDisabled,
    isDisabled = _ref2$isDisabled === void 0 ? false : _ref2$isDisabled;
  return jsx("div", {
    css: [appearance === 'default' && !isDisabled && fg('platform-component-visual-refresh') && defaultAppearanceContainerStyles, splitButtonStyles]
  }, children);
};

/**
 * __Split Button__
 *
 * A split button lets people perform an action or choose from a small group of similar actions.
 *
 * - [Examples](https://atlassian.design/components/button/split-button/examples)
 * - [Code](https://atlassian.design/components/button/split-button/code)
 * - [Usage](https://atlassian.design/components/button/split-button/usage)
 */
export var SplitButton = function SplitButton(_ref3) {
  var children = _ref3.children,
    _ref3$appearance = _ref3.appearance,
    appearance = _ref3$appearance === void 0 ? 'default' : _ref3$appearance,
    _ref3$spacing = _ref3.spacing,
    spacing = _ref3$spacing === void 0 ? 'default' : _ref3$spacing,
    _ref3$isDisabled = _ref3.isDisabled,
    isDisabled = _ref3$isDisabled === void 0 ? false : _ref3$isDisabled;
  var _getActions = getActions(children),
    PrimaryAction = _getActions.PrimaryAction,
    SecondaryAction = _getActions.SecondaryAction;
  return jsx(SplitButtonContainer, {
    appearance: appearance,
    isDisabled: isDisabled
  }, jsx(SplitButtonContext.Provider, {
    value: {
      appearance: appearance,
      spacing: spacing,
      isDisabled: isDisabled
    }
  }, jsx("div", {
    css: primaryButtonStyles
  }, PrimaryAction), jsx(Divider, {
    appearance: appearance,
    spacing: spacing,
    isDisabled: isDisabled
  }), jsx("div", {
    css: secondaryButtonStyles
  }, SecondaryAction)));
};
// TODO: Fill in the component {description} and ensure links point to the correct {packageName} location.
// Remove links that the component does not have (such as usage). If there are no links remove them all.
/**
 * __Split button with slots__
 *
 * A split button with slots {description}.
 *
 * - [Examples](https://atlassian.design/components/{packageName}/examples)
 * - [Code](https://atlassian.design/components/{packageName}/code)
 * - [Usage](https://atlassian.design/components/{packageName}/usage)
 */
export var SplitButtonWithSlots = function SplitButtonWithSlots(_ref4) {
  var primaryAction = _ref4.primaryAction,
    secondaryAction = _ref4.secondaryAction,
    _ref4$appearance = _ref4.appearance,
    appearance = _ref4$appearance === void 0 ? 'default' : _ref4$appearance,
    _ref4$spacing = _ref4.spacing,
    spacing = _ref4$spacing === void 0 ? 'default' : _ref4$spacing,
    _ref4$isDisabled = _ref4.isDisabled,
    isDisabled = _ref4$isDisabled === void 0 ? false : _ref4$isDisabled;
  return jsx(SplitButtonContainer, {
    appearance: appearance,
    isDisabled: isDisabled
  }, jsx(SplitButtonContext.Provider, {
    value: {
      appearance: appearance,
      spacing: spacing,
      isDisabled: isDisabled
    }
  }, jsx("div", {
    css: primaryButtonStyles
  }, primaryAction), jsx(Divider, {
    appearance: appearance,
    spacing: spacing,
    isDisabled: isDisabled
  }), jsx("div", {
    css: secondaryButtonStyles
  }, secondaryAction)));
};